import { X, ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { addOrderToHistory } from '@/app/utils/orderHistory';

interface PersonaProCheckoutFormProps {
  onClose: () => void;
}

export function PersonaProCheckoutForm({ onClose }: PersonaProCheckoutFormProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'venmo' | 'card' | 'paypal'>('venmo');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    avatarId: '',
    script: '',
    avatarSetting: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    mailingAddress: '',
    state: '',
    country: '',
    zipCode: '',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(' ') : cleaned;
  };

  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const handleCardNumberChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 16) {
      handleInputChange('cardNumber', formatCardNumber(cleaned));
    }
  };

  const handleExpiryChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 4) {
      handleInputChange('expiryDate', formatExpiryDate(cleaned));
    }
  };

  const handleCvvChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 4) {
      handleInputChange('cvv', cleaned);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.avatarId || !formData.script) {
      alert('Please fill in all required product details');
      return;
    }

    if (!agreedToTerms) {
      alert('Please agree to the Ethical and Moral Agreement');
      return;
    }

    if (paymentMethod === 'card' && (!formData.cardNumber || !formData.expiryDate || !formData.cvv || !formData.cardholderName || !formData.zipCode)) {
      alert('Please fill in all payment details');
      return;
    }

    // Word count validation for script
    const scriptWords = formData.script.trim().split(/\s+/).length;
    if (scriptWords < 60 || scriptWords > 70) {
      alert('Script must be between 60-70 words');
      return;
    }

    setIsProcessing(true);

    try {
      // TODO: Integrate with payment processor
      // TODO: Send order details to cara@oratf.info
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate transaction ID
      const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      
      // Prepare script with metadata
      const currentTimestamp = Date.now();
      const scriptWithMetadata = {
        content: formData.script,
        submittedBy: formData.email,
        submittedAt: currentTimestamp,
        wordCount: formData.script.trim().split(/\s+/).filter(w => w).length
      };
      
      // Add order to history
      const orderData = {
        timestamp: new Date().toISOString(),
        userId: `USER-${Date.now()}`,
        userEmail: formData.email,
        orderType: 'alacarte',
        productName: 'Persona Pro Video Intro',
        amount: 24,
        status: 'pending',
        paymentMethod: paymentMethod,
        transactionId: transactionId,
        customerId: formData.email,
        scripts: [scriptWithMetadata],
        avatarId: formData.avatarId,
      };
      addOrderToHistory(orderData);
      
      alert(`✅ Payment successful!\n\nYour Persona Pro Video Intro order has been placed.\n\nA confirmation email has been sent to ${formData.email}.\n\nOur team will contact you within 24-48 hours to begin production.`);
      
      onClose();
    } catch (error: any) {
      alert(`Payment failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const wordCount = formData.script.trim().split(/\s+/).filter(w => w).length;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-2">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl border border-gray-300 my-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-b from-green-600 to-emerald-700 p-4 rounded-t-xl relative">
          <button
            onClick={onClose}
            className="absolute top-2 left-2 text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
          >
            <ArrowLeft className="size-4" />
          </button>
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
          >
            <X className="size-4" />
          </button>
          <div className="text-center pt-4">
            <h2 className="text-lg font-bold text-white mb-1">Order Persona Pro Video Intro</h2>
            <p className="text-green-100 text-xs">
              One-time payment • No subscription required
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Product Description */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
            <h3 className="font-bold text-base text-black mb-1">AI Pro Level 2</h3>
            <p className="text-xs text-green-600 font-semibold mb-2">No monthly commitment</p>
            <div className="space-y-1">
              <p className="text-sm font-bold text-black">Persona Pro Video Intro</p>
              <p className="text-lg font-bold text-green-600">$24/one-time</p>
              <p className="text-xs text-gray-700">Based on Existing Avatar (ID required)</p>
              <ul className="text-xs space-y-0.5 mt-1">
                <li className="!text-black">• 30-45 second custom AI avatar creation</li>
                <li className="!text-black">• Your script</li>
                <li className="!text-black">• No revisions</li>
              </ul>
            </div>
          </div>

          {/* Your Name */}
          <div>
            <label className="block text-xs font-semibold text-black mb-1">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="John Doe"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
              required
            />
          </div>

          {/* Email Address */}
          <div>
            <label className="block text-xs font-semibold text-black mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
              required
            />
          </div>

          {/* Organization */}
          <div>
            <label className="block text-xs font-semibold text-black mb-1">
              Organization <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.organization}
              onChange={(e) => handleInputChange('organization', e.target.value)}
              placeholder="Your Organization Name"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
              required
            />
          </div>

          {/* Existing Avatar ID */}
          <div>
            <label className="block text-xs font-semibold text-black mb-1">
              Existing Avatar ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.avatarId}
              onChange={(e) => handleInputChange('avatarId', e.target.value)}
              placeholder="Enter your Avatar ID (e.g., CARA123)"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
              required
            />
            <p className="text-[10px] text-gray-600 mt-1">
              You must have an existing avatar to order this product. Enter your Avatar ID.
            </p>
          </div>

          {/* Avatar Setting */}
          <div>
            <label className="block text-xs font-semibold text-black mb-1">
              Avatar Setting <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.avatarSetting}
              onChange={(e) => handleInputChange('avatarSetting', e.target.value)}
              placeholder="Describe the setting you want to see the avatar in (e.g., modern office, outdoor park, professional studio)"
              maxLength={256}
              rows={3}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
              required
            />
            <p className="text-xs mt-1 text-gray-600">
              {formData.avatarSetting.length}/256 characters
            </p>
          </div>

          {/* Script */}
          <div>
            <label className="block text-xs font-semibold text-black mb-1">
              Script (60-70 words) <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.script}
              onChange={(e) => handleInputChange('script', e.target.value)}
              placeholder="Your video will communicate this exact script - check it twice!"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black resize-none h-32 text-sm"
              required
            />
            <div className={`text-xs mt-1 ${wordCount >= 60 && wordCount <= 70 ? 'text-green-600' : 'text-orange-600'}`}>
              {wordCount} words {wordCount < 60 ? '(minimum 60)' : wordCount > 70 ? '(maximum 70)' : '✓'}
            </div>
          </div>

          {/* Ethical Agreement */}
          <div className="border-2 border-gray-300 rounded-lg p-3 bg-gray-50">
            <h3 className="font-bold text-sm text-black mb-2">Ethical and Moral Agreement</h3>
            <p className="text-[10px] text-gray-700 mb-3">
              Check the box to indicate you're 18 years of age or older. This video will be used for positive messaging and information integrity. You will not use this to cyber bully, intimidate, incite violence or share private information that could cause damages.
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 size-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                required
              />
              <span className="text-xs text-black">
                I agree to the Ethical and Moral Agreement <span className="text-red-500">*</span>
              </span>
            </label>
          </div>

          {/* Payment Details */}
          <div className="border-t-2 border-green-200 pt-4">
            <h3 className="font-bold text-base text-black mb-3">Payment Details</h3>
            
            {/* Payment Method Selection */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-black mb-2">
                Select Payment Method <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('venmo')}
                  className={`flex-1 p-3 border-2 rounded-lg transition-all flex flex-col items-center gap-1 ${
                    paymentMethod === 'venmo'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-300 bg-white hover:border-green-400'
                  }`}
                  aria-label="Venmo payment method"
                >
                  <div className="text-xl">💳</div>
                  <span className="text-xs font-semibold text-black">Venmo</span>
                  <span className="text-[10px] text-green-600 font-medium">Active</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('paypal')}
                  className={`flex-1 p-3 border-2 rounded-lg transition-all flex flex-col items-center gap-1 ${
                    paymentMethod === 'paypal'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-300 bg-white hover:border-green-400'
                  }`}
                  aria-label="PayPal payment method"
                >
                  <div className="text-xl">🅿️</div>
                  <span className="text-xs font-semibold text-black">PayPal</span>
                  <span className="text-[10px] text-green-600 font-medium">Active</span>
                </button>
              </div>
            </div>

            {paymentMethod === 'venmo' ? (
              // Venmo Section
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-black">
                    You'll be redirected to Venmo to complete your purchase securely.
                  </p>
                </div>
              </div>
            ) : paymentMethod === 'card' ? (
              <div className="space-y-3 opacity-50 pointer-events-none">
                {/* Accepted Cards Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <p className="text-xs text-black">
                    <span className="font-semibold">We will soon Accept:</span> Visa, Mastercard, American Express, Discover
                  </p>
                </div>

                {/* Card Number */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Card Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.cardNumber}
                    onChange={(e) => handleCardNumberChange(e.target.value)}
                    placeholder="1234 5678 9012 3456"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black font-mono text-sm"
                    required={paymentMethod === 'card'}
                    disabled
                  />
                </div>

                {/* Expiry and CVV */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-black mb-1">
                      Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.expiryDate}
                      onChange={(e) => handleExpiryChange(e.target.value)}
                      placeholder="MM/YY"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black font-mono text-sm"
                      required={paymentMethod === 'card'}
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-black mb-1">
                      CVV <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.cvv}
                      onChange={(e) => handleCvvChange(e.target.value)}
                      placeholder="123"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black font-mono text-sm"
                      required={paymentMethod === 'card'}
                      disabled
                    />
                  </div>
                </div>

                {/* Cardholder Name */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Cardholder Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.cardholderName}
                    onChange={(e) => handleInputChange('cardholderName', e.target.value)}
                    placeholder="John Smith"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
                    required={paymentMethod === 'card'}
                    disabled
                  />
                </div>

                {/* Mailing Address */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Mailing Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.mailingAddress}
                    onChange={(e) => handleInputChange('mailingAddress', e.target.value)}
                    placeholder="123 Main St, Apt 4B"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
                    required={paymentMethod === 'card'}
                    disabled
                  />
                </div>

                {/* State */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="Washington"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
                    required={paymentMethod === 'card'}
                    disabled
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="United States"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
                    required={paymentMethod === 'card'}
                    disabled
                  />
                </div>

                {/* ZIP Code */}
                <div>
                  <label className="block text-xs font-semibold text-black mb-1">
                    ZIP Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder="98107"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-green-600 focus:outline-none text-black text-sm"
                    required={paymentMethod === 'card'}
                    disabled
                  />
                </div>
              </div>
            ) : (
              // PayPal Section
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-black">
                    You'll be redirected to PayPal to complete your purchase securely.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isProcessing}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing Payment...
              </>
            ) : (
              <>
                Complete Purchase - $24
              </>
            )}
          </button>

          {/* Security Note */}
          <p className="text-[10px] text-gray-600 text-center">
            🔒 Secure payment processing • Your information is encrypted and protected
          </p>
        </form>
      </div>
    </div>
  );
}